import { useCallback, useEffect, useState } from 'react';
import { Button, Input } from '../../../shared/ui';
import { EyeOff, ChevronUp, ChevronDown, Smartphone, AlertCircle } from 'lucide-react';
import { Clock, Eye, InfoCircle, Refresh } from 'iconsax-reactjs';
import BackupCodeModal from '../../../features/auth/components/BackupCodeModal';
import { cn, handleCopy, handleDownloadBackupCodes, handlePrintBackupCodes } from '../../../shared/lib/utils';
import useProfile from '../hooks/useProfile';
import { toast } from 'sonner';
import OtpModal from './OtpModal';
import { useNavigate } from 'react-router';
import { useDispatch } from 'react-redux';
import { logout } from '../../../redux/slices/authSlice';

const locationDetails = [
  {
    location: 'Nigeria (LA)',
    device: 'Chrome (Mac OS)',
    ip: '102.99.94.197',
    lastActive: 'Now',
    status: 'current' as const,
  },
  {
    location: 'Nigeria (LA)',
    device: 'Arc (Mac OS)',
    ip: '101.99.94.197',
    lastActive: 'Yesterday',
    status: 'logout' as const,
  },
  {
    location: 'Nigeria (FCT)',
    device: 'Chrome (Mac OS)',
    ip: '101.99.94.197',
    lastActive: '3 months ago',
    status: 'expired' as const,
  },
]

const Security = () => {
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [twoFAOpen, setTwoFAOpen] = useState(true)
  const [recoveryOpen, setRecoveryOpen] = useState(false)
  const [openModal, setOpenModal] = useState(false)
  const [showCloseIcon, setShowCloseIcon] = useState(false)
  const [sendLoginNotification, setSendLoginNotification] = useState(false)
  const [backupCodes, setBackupCodes] = useState([])
  const [otpModal, setOtpModal] = useState<'change-password' | 'setup-2fa' | 'regenerate-backup-codes' | ''>('')
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const { handleLogoutAllAdminSession, logoutAllAdminSessionPending, profileData, isLoadingProfileData, handleAdminPreference, handleAdminChangePassword, changePasswordPending } = useProfile()
  console.log({profileData})
  useEffect(() => {
    if (!isLoadingProfileData && profileData) {
      setSendLoginNotification(profileData?.sendLoginNotification!)
    }
  }, [isLoadingProfileData, profileData])

  const toggleSendLoginNotification = async () => {
    try {
      setSendLoginNotification(!sendLoginNotification)
      await handleAdminPreference({ sendLoginNotification: !sendLoginNotification })
      toast.success('Send login notification updated successfully')
    } catch (error) {
      console.log(error)
      toast.error('Failed to update send login notification')
    }
  }

    const handleInactivityLogout = useCallback(() => {
      localStorage.removeItem('token')
      dispatch(logout())
      navigate('/', { replace: true })
    }, [dispatch, navigate])

  const isSamePasswordValue = password.length >= 8 && confirmPassword.length >= 8 && password !== '';

  return (
    <>
      <section className="space-y-7 rounded-lg md:w-[910px]">
        <div className="flex flex-col border-b border-gray-60 pb-4 items-start gap-5 md:flex-row pl-2">
          <div className="md:w-4/12">
            <p className="text-lg font-[450] text-gray-30 leading-[-2.5%]">Password</p>
          </div>
          <form className="space-y-6 w-full md:w-8/12 ">
            <div className="space-y-1">
              <p className="text-sm font-[450] text-gray-30">Current password</p>
              <Input
                type={showPassword ? "text" : "password"}
                placeholder="Enter current password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-[56px] px-4 rounded-[8px] bg-primary-80 border-none"
                endIcon={
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="text-[#6F739B]">
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                }
              />
            </div>
            <div className="space-y-1">
              <p className="text-sm font-[450] text-gray-30">New password</p>
              <Input
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Enter new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="h-[56px] px-4 rounded-[8px] bg-primary-80 border-none"
                endIcon={
                  <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="text-[#6F739B]">
                    {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                }
              />
            </div>
            {isSamePasswordValue && <div className="space-y-1">
              {(!isSamePasswordValue) && (
                <div className={cn("flex items-center gap-2 text-[12px] font-[450]", isSamePasswordValue ? "text-[#6F739B]" : "text-[#E24B4B]")}>
                  {isSamePasswordValue ? <InfoCircle size={16} className="text-[#008B52]" /> : <AlertCircle size={16} className="text-[#EAAC08]" />}
                  <span>Passwords must be minimum of 8 character</span>
                </div>
              )}
            </div>}

            {/* Save Changes Button */}
            <Button
              variant="primary"
              size="lg"
              onClick={() => setOtpModal('change-password')}
              disabled={!isSamePasswordValue}
              className="w-fit h-10 mt-0 bg-primary-20 hover:bg-primary-30 text-base font-[450] rounded-[8px] px-4 py-2"
            >
              {false ? "Saving..." : "Save changes"}
            </Button>
          </form>
        </div>
        <div className="flex flex-col border-b border-gray-60 pb-4 items-start gap-5 md:flex-row pl-2">
          <div className="md:w-4/12">
            <p className="text-lg font-[450] text-gray-30 leading-[-2.5%]">Two-factor authentication (2FA)</p>
          </div>
          <div className="space-y-3 md:w-8/12">

            <div className="border border-gray-60 rounded-xl overflow-hidden">
              <button
                className="w-full flex items-start justify-between p-4 text-left"
                onClick={() => setTwoFAOpen(!twoFAOpen)}
              >
                <div>
                  <p className="text-sm font-[600] text-gray-30">Set up two-step authentication</p>
                  <p className="text-sm font-[350] text-gray-70 mt-0.5">
                    Increase your admin account's security with an extra layer of verification.
                  </p>
                </div>
                <span className="ml-4 mt-0.5 text-gray-70 flex-shrink-0">
                  {twoFAOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </span>
              </button>

              {twoFAOpen && (
                <div className="px-4 pb-4">
                  {/* Table header */}
                  <div className="grid grid-cols-[1fr_1fr_1fr] justify-between gap-x-4 pb-2">
                    <p className="text-sm font-[350] text-gray-70">Method</p>
                    <p className="text-sm font-[350] text-gray-70 pl-8">Date added</p>
                    <p className="text-sm font-[350] text-gray-70">
                    </p>
                  </div>
                  {/* Table row */}
                  <div className="grid grid-cols-[1fr_1fr_1fr] justify-between gap-x-4 items-center py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-70 rounded-full p-1 bg-primary-70">
                        <Smartphone size={16} />
                      </span>
                      <p className="text-sm font-[600] text-gray-30">Authenticator app</p>
                    </div>
                    <p className="text-sm font-[350] text-gray-30 pl-8">June 06, 2024</p>
                    <button
                      className="text-sm font-[600] ml-auto text-primary-20 hover:text-primary-30 transition-colors"
                      onClick={() => setOtpModal('setup-2fa')}
                    >
                      Change
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Recovery options card */}
            <div className="border border-gray-60 rounded-xl overflow-hidden">
              <button
                className="w-full flex items-start justify-between p-4 text-left"
                onClick={() => setRecoveryOpen(!recoveryOpen)}
              >
                <div>
                  <p className="text-base font-[600] text-gray-30">Recovery options</p>
                  <p className="text-sm font-[350] text-gray-70 mt-0.5">
                    This will let you regain access if you ever lose your phone.
                  </p>
                </div>
                <span className="ml-4 mt-0.5 text-gray-70 flex-shrink-0">
                  {recoveryOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </span>
              </button>

              {recoveryOpen && (
                <div className="px-4 pb-4">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-[450] text-gray-30">Backup verification codes</p>

                    <Button
                      variant="outline"
                      size="sm"
                      className="h-10 text-sm gap-2 font-[450] px-2 py-[2px] rounded-[8px] border-2 border-gray-60"
                      onClick={() => setOtpModal('regenerate-backup-codes')}
                    >
                      <Refresh size={16} />
                      Generate new codes
                    </Button>
                  </div>
                  <p className="italic text-gray-30 text-sm flex gap-2 items-center mt-2 font-[350]"><Clock size={16} /> Last generated: Aug 14, 2025</p>
                </div>
              )}
            </div>

          </div>

        </div>
        <div className="flex flex-col border-b border-gray-60 pb-4 items-start gap-5 md:flex-row pl-2">
          <div className="md:w-4/12">
            <p className="text-lg font-[450] text-gray-30 leading-[-2.5%]">Login sessions</p>
          </div>
          <div className="space-y-6 md:w-8/12 ">
            <div className="flex items-center justify-between">
              <p className="text-sm font-[450] text-gray-30">View all active sessions</p>

              <Button
                variant="outline"
                size="sm"
                disabled={logoutAllAdminSessionPending}
                className="h-auto text-sm font-[450] px-2 py-[2px] rounded-[8px] border-2 border-gray-60"
                onClick={async () => {
                  await handleLogoutAllAdminSession()
                  handleInactivityLogout()
                 }}
              >
                {logoutAllAdminSessionPending ? "Logging out..." : "Log out of all other sessions"}
              </Button>
            </div>

            {/* Sessions Table */}
            <div className="w-full">
              {/* Table Header */}
              <div className="grid text-gray-70 grid-cols-[1.5fr_1.5fr_1.5fr_1fr_0.8fr] gap-x-4 px-0 pb-2">
                <p className="text-sm font-[350] ">Location</p>
                <p className="text-sm font-[350] ">Device</p>
                <p className="text-sm font-[350] ">IP Address</p>
                <p className="text-sm font-[350] ">Last active</p>
                <p className="text-sm font-[350] "></p>
              </div>

              {/* Session Rows */}
              {locationDetails.map((session, idx) => (
                <div
                  key={idx}
                  className="grid grid-cols-[1.5fr_1.5fr_1.5fr_1fr_0.8fr] gap-x-4 py-3 border-t border-gray-60 items-center"
                >
                  <p className="text-sm font-[600] text-gray-30">{session.location}</p>
                  <p className="text-sm font-[350] text-gray-30">{session.device}</p>
                  <p className="text-sm font-[350] text-gray-30">{session.ip}</p>
                  <p className="text-sm font-[350] text-gray-30">{session.lastActive}</p>
                  <div>
                    {session.status === 'current' && (
                      <p className="text-sm font-[350] text-gray-20">Current</p>
                    )}
                    {session.status === 'logout' && (
                      <button
                        className="text-sm font-[600] text-gray-30 hover:text-primary-20 transition-colors"
                        onClick={() => { }}
                      >
                        Log out
                      </button>
                    )}
                    {session.status === 'expired' && (
                      <p className="text-sm font-[350] text-gray-20">Expired</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="flex flex-col border-b border-gray-60 pb-4 items-start gap-5 md:flex-row pl-2">
          <div className="md:w-4/12">
            <p className="text-lg font-[450] text-gray-30 leading-[-2.5%]">Alerts</p>
          </div>
          <div className="space-y-6 md:w-8/12 ">
            <div className="flex justify-between items-center gap-2">
              <div>
                <p className="text-sm font-[450] text-gray-30">Login</p>
                <p className="text-sm font-[350] text-gray-30">Get email alerts when logging in from a new device or location.</p>
              </div>
              <div className="flex items-center justify-center">
                <input
                  type="checkbox"
                  id="switch"
                  className='border'
                  checked={sendLoginNotification}
                  // onChange={() => setSendLoginNotification(!sendLoginNotification)}
                  onChange={toggleSendLoginNotification}
                />
                <label htmlFor="switch">Toggle</label>
              </div>
            </div>
          </div>
        </div>
      </section>
      <BackupCodeModal
        isOpen={openModal}
        onOpenChange={() => {
          setOpenModal(false)
        }}
        backupCodes={backupCodes}
        handleCopy={() => handleCopy(backupCodes, setShowCloseIcon)}
        handleDownload={() => handleDownloadBackupCodes(backupCodes, setShowCloseIcon)}
        handlePrint={() => handlePrintBackupCodes(backupCodes, setShowCloseIcon)}
        showCloseIcon={showCloseIcon}
      />
      <OtpModal
        isOpen={otpModal}
        onOpenChange={() => {
          setOtpModal('')
         
        }}
        onSuccess={(data: any) => {
          if (otpModal === 'change-password') {
            setPassword('')
            setConfirmPassword('')
          }
          if (otpModal === 'regenerate-backup-codes') {
            setOpenModal(true)
            setBackupCodes(data)
          }
        }}
        handleAdminChangePassword={async (mfaCode: string) => await handleAdminChangePassword({ oldPassword: password, newPassword: confirmPassword, mfaCode })}
        isLoading={changePasswordPending}
      />
    </>
  )
}

export default Security
